import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FaGlobeAmericas,
  FaMapMarkerAlt,
  FaUsers,
  FaChartLine,
} from "react-icons/fa";

const TalksMap = () => {
  const [selectedRegion, setSelectedRegion] = useState("all");

  // Organized locations by region
  const locationsByRegion = {
    "North America": [
      "Boston, MA",
      "Bay Area, CA",
      "Las Vegas, NV",
      "New York, NY",
      "Raleigh, NC",
      "Austin, TX",
      "Denver, CO",
      "Los Angeles, CA",
      "Salt Lake City, UT",
      "Chicago, IL",
      "Toronto, Canada",
    ],
    Europe: [
      "London, UK",
      "Amsterdam, Netherlands",
      "Paris, France",
      "Milan, Italy",
      "Berlin, Germany",
    ],
    "Asia Pacific": [
      "Bangalore, India",
      "Mumbai, India",
      "Beijing, China",
      "Jakarta, Indonesia",
      "Tokyo, Japan",
      "Sydney, Australia",
      "Auckland, New Zealand",
      "Singapore",
      "Seoul, South Korea",
      "Tel Aviv, Israel",
    ],
    "South America": ["São Paulo, Brazil"],
  };

  // Statistics
  const stats = [
    { icon: FaGlobeAmericas, value: "6", label: "Continents" },
    { icon: FaMapMarkerAlt, value: "28+", label: "Cities" },
    { icon: FaUsers, value: "10K+", label: "Attendees Reached" },
    { icon: FaChartLine, value: "50+", label: "Conferences" },
  ];

  // Get all locations or filtered by region
  const getFilteredLocations = () => {
    if (selectedRegion === "all") {
      return Object.entries(locationsByRegion).flatMap(([region, cities]) =>
        cities.map((city) => ({ city, region })),
      );
    }
    return (locationsByRegion[selectedRegion] || []).map((city) => ({
      city,
      region: selectedRegion,
    }));
  };

  const filteredLocations = getFilteredLocations();

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900"></div>
      <div className="absolute inset-0 bg-hero-pattern opacity-5"></div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Global Impact
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Sharing knowledge and insights across continents, reaching thousands
            of professionals worldwide
          </p>
        </motion.div>

        {/* Statistics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center"
            >
              <stat.icon className="text-4xl text-blue-400 mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Region Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          <button
            onClick={() => setSelectedRegion("all")}
            className={`px-6 py-2 rounded-full transition-all ${
              selectedRegion === "all"
                ? "bg-blue-600 text-white"
                : "bg-white/10 text-gray-300 hover:bg-white/20"
            }`}
          >
            All Regions
          </button>
          {Object.keys(locationsByRegion).map((region) => (
            <button
              key={region}
              onClick={() => setSelectedRegion(region)}
              className={`px-6 py-2 rounded-full transition-all ${
                selectedRegion === region
                  ? "bg-blue-600 text-white"
                  : "bg-white/10 text-gray-300 hover:bg-white/20"
              }`}
            >
              {region}
            </button>
          ))}
        </motion.div>

        {/* Locations Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
        >
          {filteredLocations.map((location, index) => (
            <motion.div
              key={`${location.region}-${location.city}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              whileHover={{ scale: 1.05 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4 hover:bg-white/10 hover:border-blue-500/50 transition-all"
            >
              <div className="flex items-center gap-3">
                <FaMapMarkerAlt className="text-blue-400 flex-shrink-0" />
                <div className="text-left">
                  <div className="text-white font-medium">{location.city}</div>
                  <div className="text-xs text-gray-400">{location.region}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Visual World Map Alternative */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
          className="mt-16 relative"
        >
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold text-white mb-6">
                Speaking Engagements Across the Globe
              </h3>

              {/* World Map SVG Illustration */}
              <div className="relative mb-8">
                <svg
                  viewBox="0 0 1000 500"
                  className="w-full h-auto"
                  style={{ maxHeight: "300px" }}
                >
                  {/* Simple world map outline */}
                  <g
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    className="text-blue-400/30"
                  >
                    {/* North America */}
                    <path d="M 200 150 Q 150 100 100 150 T 50 200 Q 100 250 150 200 T 200 150" />
                    {/* South America */}
                    <path d="M 150 300 Q 125 275 100 300 T 75 350 Q 100 400 125 375 T 150 300" />
                    {/* Europe */}
                    <path d="M 450 125 Q 475 100 500 125 T 525 150 Q 500 175 475 150 T 450 125" />
                    {/* Africa */}
                    <path d="M 475 250 Q 450 225 425 250 T 400 300 Q 425 350 450 325 T 475 250" />
                    {/* Asia */}
                    <path d="M 700 150 Q 750 100 800 150 T 850 200 Q 800 250 750 200 T 700 150" />
                    {/* Australia */}
                    <path d="M 800 350 Q 825 325 850 350 T 875 375 Q 850 400 825 375 T 800 350" />
                  </g>

                  {/* Animated dots for locations */}
                  {[
                    { x: 150, y: 180 }, // North America
                    { x: 125, y: 350 }, // South America
                    { x: 475, y: 140 }, // Europe
                    { x: 750, y: 180 }, // Asia
                    { x: 825, y: 360 }, // Australia
                  ].map((pos, index) => (
                    <motion.circle
                      key={index}
                      cx={pos.x}
                      cy={pos.y}
                      r="5"
                      fill="currentColor"
                      className="text-blue-400"
                      initial={{ scale: 0 }}
                      animate={{ scale: [1, 1.5, 1] }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: index * 0.3,
                      }}
                    />
                  ))}
                </svg>
              </div>

              <p className="text-gray-300 text-lg">
                From major tech hubs to emerging markets, I've had the privilege
                of speaking at conferences, meetups, and workshops around the
                world, sharing insights on cloud security, DevSecOps, and the
                future of vulnerability management.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TalksMap;
